import React, { useReducer, createContext } from 'react';
import {
  ImageUploadActionType,
  SelectIdTypeStep,
  EndTakePhotoPromptStep,
  UpdateDocumentsStep,
  InitDocumentsStep,
  EndSelectIdTypeStep,
  CloseSelectIdTypeStep,
  CloseTakePhotoStep,
  InitUploadPhotoStep,
  SubmitPhotoStep,
  InitTakePhotoStep,
  TakePhotoTipStep,
  EndTakePhotoTipStep,
  AddIdDocsStep,
  RemoveIdDocsStep,
  TakePhotoStep,
  UpdateTakePhotoStep,
  UpdateCurrentDocumentsStep,
  SetAllowedFileTypesStep,
  AddSelectedFilesStep,
  SetStepIndexStep,
  RemoveDocumentStep,
  RetakePhotoStep,
  RetryUploadPhotoStep,
} from '../types/image-upload-reducer-actions';
import IdGuideModal from '../components/id-guide-modal/id-guide-modal';
import {
  DocumentWithOptionalBody,
  IDDocsUploaded,
  IdItem,
  OnboardingDocumentTypeEnum,
} from '../types/upload-documents';
import SelectIdTypeModal from '../components/select-id-type-modal/select-id-type-modal';
import { stripWhitespace } from 'src/helpers/formatting';
import { TakePhotoTipsModal } from '../components';
import { FileTypes } from 'src/features/common/components/file-upload/file-upload';

type ImageUploadAction =
  | InitDocumentsStep
  | UpdateDocumentsStep
  | RemoveDocumentStep
  | AddIdDocsStep
  | RemoveIdDocsStep
  | SelectIdTypeStep
  | EndSelectIdTypeStep
  | CloseSelectIdTypeStep
  | InitUploadPhotoStep
  | RetryUploadPhotoStep
  | SubmitPhotoStep
  | CloseTakePhotoStep
  | EndTakePhotoPromptStep
  | InitTakePhotoStep
  | TakePhotoStep
  | RetakePhotoStep
  | UpdateTakePhotoStep
  | TakePhotoTipStep
  | EndTakePhotoTipStep
  | UpdateCurrentDocumentsStep
  | SetAllowedFileTypesStep
  | AddSelectedFilesStep
  | SetStepIndexStep;

export enum ImageUploadJourney {
  UPLOAD = 'upload',
  CAMERA = 'camera',
}

type ImageUploadState = {
  documents: DocumentWithOptionalBody[];
  ids: IdItem[];
  refugeeIds: IdItem[];
  asylumIds: IdItem[];
  journey?: ImageUploadJourney;
  imageJoinEnabled?: boolean;
  requestBothSides?: boolean;
  currentDocuments: DocumentWithOptionalBody[];
  currentIdNumber?: string;
  currentIdDocType: OnboardingDocumentTypeEnum;
  documentType: OnboardingDocumentTypeEnum;
  idDocsUploaded: IDDocsUploaded[];
  stepName?: ImageUploadActionType;
  component?: React.ReactElement;
  showModal?: boolean;
  uploadPhoto: boolean;
  capturedImage?: string;
  takePhoto: boolean;
  openCamera: boolean;
  allowedFileTypes?: FileTypes[];
  selectedFile?: File;
  stepIndex: number;
  capturedDocName?: string;
  maxFileSize?: number;
};

type ImageUploadContextType = {
  imageUploadState: ImageUploadState;
  imageUploadDispatch: React.Dispatch<ImageUploadAction>;
};

export const ImageUploadContext = createContext<ImageUploadContextType | undefined>(undefined);

type ImageUploadProviderProps = {
  children: React.ReactNode;
};

export function ImageUploadProvider({ children }: ImageUploadProviderProps) {
  const stepsMap = [
    {
      name: ImageUploadActionType.SELECT_ID_TYPE,
      component: <SelectIdTypeModal />,
    },
    {
      name: ImageUploadActionType.TAKE_PHOTO,
      component: <IdGuideModal />,
    },
    {
      name: ImageUploadActionType.TAKE_PHOTO_TIP,
      component: <TakePhotoTipsModal />,
    },
  ];

  const capturedDocNames = new Map<number, string>([
    [0, 'Front'],
    [1, 'Back'],
  ]);

  const idTypesList = [
    OnboardingDocumentTypeEnum.ID_BOOK,
    OnboardingDocumentTypeEnum.ID_CARD,
    OnboardingDocumentTypeEnum.PASSPORT,
  ];

  const addIdDocsUploaded = (idDocsUploaded: IDDocsUploaded[], doc: DocumentWithOptionalBody) => {
    return idDocsUploaded?.map((idDoc) => {
      if (doc?.idNumber && idDoc.idNumber === doc.idNumber) {
        return {
          ...idDoc,
          numOfGreenBooks:
            doc.documentType === OnboardingDocumentTypeEnum.ID_BOOK
              ? idDoc.numOfGreenBooks + 1
              : idDoc.numOfGreenBooks,
          numOfSmartCards:
            doc.documentType === OnboardingDocumentTypeEnum.ID_CARD
              ? idDoc.numOfSmartCards + 1
              : idDoc.numOfSmartCards,
          numOfPassports:
            doc.documentType === OnboardingDocumentTypeEnum.PASSPORT
              ? idDoc.numOfPassports + 1
              : idDoc.numOfPassports,
        };
      }
      return idDoc;
    });
  };

  const addIdDocsUploadedWithIndex = (
    idDocsUploaded: IDDocsUploaded[],
    docs: DocumentWithOptionalBody[],
    index: number,
  ) => {
    const doc = docs[index];
    return idDocsUploaded?.map((idDoc) => {
      if (doc?.idNumber && idDoc.idNumber === doc.idNumber) {
        return {
          ...idDoc,
          numOfGreenBooks:
            doc.documentType === OnboardingDocumentTypeEnum.ID_BOOK
              ? idDoc.numOfGreenBooks + 1
              : idDoc.numOfGreenBooks,
          numOfSmartCards:
            doc.documentType === OnboardingDocumentTypeEnum.ID_CARD
              ? idDoc.numOfSmartCards + 1
              : idDoc.numOfSmartCards,
          numOfPassports:
            doc.documentType === OnboardingDocumentTypeEnum.PASSPORT
              ? idDoc.numOfPassports + 1
              : idDoc.numOfPassports,
        };
      }
      return idDoc;
    });
  };

  const removeIdDocsUploaded = (
    idDocsUploaded: IDDocsUploaded[],
    doc: DocumentWithOptionalBody,
  ) => {
    return idDocsUploaded?.map((idDoc) => {
      if (idDoc.idNumber === doc.idNumber) {
        return {
          ...idDoc,
          numOfGreenBooks:
            doc.documentType === OnboardingDocumentTypeEnum.ID_BOOK &&
            !(idDoc.numOfGreenBooks === 0)
              ? idDoc.numOfGreenBooks - 1
              : idDoc.numOfGreenBooks,
          numOfSmartCards:
            doc.documentType === OnboardingDocumentTypeEnum.ID_CARD &&
            !(idDoc.numOfSmartCards === 0)
              ? idDoc.numOfSmartCards - 1
              : idDoc.numOfSmartCards,
          numOfPassports:
            doc.documentType === OnboardingDocumentTypeEnum.PASSPORT &&
            !(idDoc.numOfPassports === 0)
              ? idDoc.numOfPassports - 1
              : idDoc.numOfPassports,
        };
      }
      return idDoc;
    });
  };

  const resetIdDocsUploaded = (idDocsUploaded: IDDocsUploaded[]) => {
    return idDocsUploaded.map((idDoc) => {
      return {
        ...idDoc,
        numOfGreenBooks: 0,
        numOfSmartCards: 0,
        numOfPassports: 0,
      };
    });
  };

  const initIdDocsUploaded = (ids: IdItem[], initialDocs: DocumentWithOptionalBody[]) => {
    return ids.map((item) => {
      const doc = initialDocs.find(
        (doc) =>
          stripWhitespace(doc.idNumber) === stripWhitespace(item.idNumber) &&
          [
            OnboardingDocumentTypeEnum.ID_BOOK,
            OnboardingDocumentTypeEnum.ID_CARD,
            OnboardingDocumentTypeEnum.PASSPORT,
          ].includes(doc.documentType),
      );
      return {
        idNumber: stripWhitespace(doc?.idNumber),
        numOfGreenBooks: doc?.documentType === OnboardingDocumentTypeEnum.ID_BOOK ? 1 : 0,
        numOfSmartCards: doc?.documentType === OnboardingDocumentTypeEnum.ID_CARD ? 1 : 0,
        numOfPassports: doc?.documentType === OnboardingDocumentTypeEnum.PASSPORT ? 1 : 0,
      };
    });
  };

  const reducer = (state: ImageUploadState, action: ImageUploadAction): ImageUploadState => {
    switch (action.type) {
      //  Documents
      case ImageUploadActionType.INIT_DOCUMENTS: {
        const initialIdDocsUploaded = action.ids?.map((item) => ({
          idNumber: stripWhitespace(item.idNumber),
          numOfSmartCards: 0,
          numOfGreenBooks: 0,
          numOfPassports: 0,
        }));

        const idDocs = action.initialDocs.filter((doc) => idTypesList.includes(doc.documentType));
        const newState: ImageUploadState = {
          ...state,
          documents: action.initialDocs,
          ids: action.ids,
          idDocsUploaded:
            idDocs.length > 0
              ? initIdDocsUploaded(action.ids, action.initialDocs)
              : initialIdDocsUploaded,
        };
        return newState;
      }

      case ImageUploadActionType.UPDATE_CURRENT_DOCUMENTS: {
        const newState: ImageUploadState = {
          ...state,
          currentDocuments: action.docs ?? state.currentDocuments,
        };
        return newState;
      }

      case ImageUploadActionType.UPDATE_DOCUMENTS: {
        const newState: ImageUploadState = {
          ...state,
          documents: action.docs,
          currentDocuments: action.currentDocs ?? state.currentDocuments,
        };
        return newState;
      }

      case ImageUploadActionType.REMOVE_DOCUMENTS: {
        const newState: ImageUploadState = {
          ...state,
          documents: action.docs,
          idDocsUploaded: action.fileToRemove
            ? removeIdDocsUploaded(state.idDocsUploaded, action.fileToRemove)
            : state.idDocsUploaded,
          currentDocuments: [],
        };
        return newState;
      }

      case ImageUploadActionType.ADD_ID_DOCS: {
        const newState: ImageUploadState = {
          ...state,
          idDocsUploaded: addIdDocsUploaded(state.idDocsUploaded, action.doc),
        };
        return newState;
      }

      case ImageUploadActionType.SET_ALLOWED_FILE_TYPES: {
        const newState: ImageUploadState = {
          ...state,
          allowedFileTypes: action.allowedFileTypes,
          maxFileSize: action.maxFileSize,
        };
        return newState;
      }

      case ImageUploadActionType.ADD_SELECT_FILES: {
        const newState: ImageUploadState = {
          ...state,
          selectedFile: action.selectedFile,
        };
        return newState;
      }

      case ImageUploadActionType.SET_STEP_INDEX: {
        const newState: ImageUploadState = {
          ...state,
          stepIndex: action.stepIndex < 0 ? 0 : action.stepIndex,
        };

        return newState;
      }

      //  SELECT ID TYPE
      case ImageUploadActionType.SELECT_ID_TYPE: {
        const step = stepsMap.find((step) => step.name === ImageUploadActionType.SELECT_ID_TYPE);

        if (step) {
          const newState: ImageUploadState = {
            ...state,
            journey: action.journey,
            component: step.component,
            stepName: ImageUploadActionType.SELECT_ID_TYPE,
            showModal: true,
            currentDocuments: state.currentDocuments ?? [],
            documentType: action.documentType ?? state.documentType,
          };
          return newState;
        }
        return state;
      }

      case ImageUploadActionType.END_SELECT_ID_TYPE: {
        const step = stepsMap.find((step) => step.name === ImageUploadActionType.TAKE_PHOTO);
        if (step) {
          const newState: ImageUploadState = {
            ...state,
            component: step.component,
            stepName: ImageUploadActionType.TAKE_PHOTO,
            showModal: action.showModal,
            requestBothSides: action.requestBothSides,
            currentIdNumber: action.idNumber,
            currentIdDocType: action.idDocType,
            openCamera: action.openCamera,
          };
          return newState;
        }
        return state;
      }

      case ImageUploadActionType.CLOSE_SELECT_ID_TYPE: {
        const newState: ImageUploadState = {
          ...state,
          showModal: action.showModal,
          currentIdNumber: action.idNumber,
          currentDocuments: [],
          selectedFile: undefined,
        };
        return newState;
      }

      // UPLOAD PHOTO
      case ImageUploadActionType.INIT_UPLOAD_PHOTO: {
        const newState: ImageUploadState = {
          ...state,
          uploadPhoto: action.uploadPhoto,
        };
        return newState;
      }

      case ImageUploadActionType.RETRY_UPLOAD_PHOTO: {
        const newState: ImageUploadState = {
          ...state,
          uploadPhoto: action.uploadPhoto,
          currentDocuments: [],
        };
        return newState;
      }

      case ImageUploadActionType.SUBMIT_PHOTO: {
        const newState: ImageUploadState = {
          ...state,
          currentDocuments: action.documents,
          idDocsUploaded: addIdDocsUploadedWithIndex(
            state.idDocsUploaded,
            action.documents ?? state.currentDocuments,
            action.stepIndex ?? 0,
          ),
          openCamera: action?.openCamera ?? false,
        };
        return newState;
      }

      case ImageUploadActionType.CLOSE_TAKE_PHOTO: {
        const newState: ImageUploadState = {
          ...state,
          showModal: action.showModal,
          documents: state.documents.length ? state.documents : action.documents,
          selectedFile: undefined,
          currentDocuments: [],
          idDocsUploaded: resetIdDocsUploaded(state.idDocsUploaded),
        };
        return newState;
      }

      case ImageUploadActionType.END_TAKE_PHOTO_PROMPT: {
        const newState: ImageUploadState = {
          ...state,
          documents: [...state.documents, ...state.currentDocuments],
          showModal: false,
          currentDocuments: [],
          currentIdNumber: '',
          currentIdDocType: OnboardingDocumentTypeEnum.ID_TYPE,
        };
        return newState;
      }

      // TAKE PHOTO TIPS
      case ImageUploadActionType.TAKE_PHOTO_TIP: {
        const step = stepsMap.find((step) => step.name === ImageUploadActionType.TAKE_PHOTO_TIP);
        if (step) {
          const newState: ImageUploadState = {
            ...state,
            component: step.component,
            stepName: ImageUploadActionType.TAKE_PHOTO_TIP,
            showModal: action.showModal,
            documentType: action.documentType ?? state.documentType,
          };
          return newState;
        }
        return state;
      }

      case ImageUploadActionType.END_TAKE_PHOTO_TIP: {
        const newState: ImageUploadState = {
          ...state,
          openCamera: action.openCamera,
          showModal: action?.showModal ?? state.showModal,
        };
        return newState;
      }

      // TAKE PHOTO
      case ImageUploadActionType.INIT_TAKE_PHOTO: {
        const newState: ImageUploadState = {
          ...state,
          takePhoto: action.takePhoto,
          capturedImage: action.imageSrc,
          openCamera: false,
          capturedDocName: state.requestBothSides
            ? capturedDocNames.get(action.stepIndex)
            : undefined,
        };
        return newState;
      }

      case ImageUploadActionType.TAKE_PHOTO: {
        const newState: ImageUploadState = {
          ...state,
          capturedImage: action.imageSrc,
        };
        return newState;
      }

      case ImageUploadActionType.RETAKE_PHOTO: {
        const newState: ImageUploadState = {
          ...state,
          openCamera: true,
          currentDocuments: [],
        };
        return newState;
      }

      case ImageUploadActionType.UPDATE_TAKE_PHOTO: {
        const newState: ImageUploadState = {
          ...state,
          takePhoto: action.takePhoto,
        };
        return newState;
      }

      default:
        return state;
    }
  };

  const initialState: ImageUploadState = {
    documents: [],
    ids: [],
    refugeeIds: [],
    asylumIds: [],
    takePhoto: false,
    uploadPhoto: false,
    showModal: false,
    currentDocuments: [],
    currentIdDocType: OnboardingDocumentTypeEnum.ID_TYPE,
    documentType: OnboardingDocumentTypeEnum.ID_TYPE,
    idDocsUploaded: [],
    openCamera: false,
    stepIndex: 0,
  };

  const [imageUploadState, dispatch] = useReducer(reducer, initialState);

  return (
    <ImageUploadContext.Provider value={{ imageUploadState, imageUploadDispatch: dispatch }}>
      {children}
    </ImageUploadContext.Provider>
  );
}
